import React from 'react';
import styled from 'styled-components';
import { Currency, DateTime, Hours, Icon, Percent } from '~/components';
import revenueRecognitionMethods from '~/lookups/revenue-recognition-methods';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';

const metrics = {
  automated_hours: 'Billable hours',
  automated_tm_basis: 'Time & Materials revenue',
  automated_services_cost: 'Services cost',
};

const Card = styled.div`
  align-self: stretch;
  margin: -0.5rem -1rem;
  color: ${colors.grey75};
  font-size: 0.875rem;
`;

const Header = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 0.0625rem solid ${colors.grey10};
`;

const Section = styled.div`
  padding: 0.875rem 1.25rem 1rem;
  background-color: ${({ $shaded }) => ($shaded ? colors.grey5 : 'transparent')};
`;

const Label = styled.div`
  margin-bottom: 0.5rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
`;

const Provenance = styled.div`
  color: ${colors.grey55};
  font-size: 0.8125rem;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $dense }) => ($dense ? '0.4375rem' : '0.625rem')};
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
`;

const Derivation = styled(Rows)`
  padding-left: 0.75rem;
  color: ${colors.grey55};
  font-size: 0.8125rem;
`;

const Subtotal = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: 0.75rem;
  padding-top: 0.4375rem;
  border-top: 0.0625rem solid ${colors.grey10};
`;

const NestedSubtotal = styled(Subtotal)`
  margin-left: 0;
`;

const Breakdown = styled.div`
  margin-top: 0.125rem;
  padding-left: 0.5rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  line-height: 1.4;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const Amount = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $strong }) => ($strong ? weights.bold : weights.normal)};
`;

const Operator = styled(Icon)`
  margin-right: 0.375rem;
  color: ${colors.grey55};
  font-size: 0.75em;
`;

const Result = styled(Row)`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 0.0625rem solid ${colors.primary25};
  font-weight: ${weights.bold};
`;

const ResultAmount = styled.span`
  color: ${colors.accent};
  font-size: 1.25rem;
  font-weight: ${weights.light};
`;

function RemainingRevenue({ calculation }) {
  const { revenue } = calculation;

  return (
    <Card>
      <Header>
        <Label>How this amount was calculated</Label>
        <Provenance>
          Generated on <DateTime value={calculation.generatedAt} format={dateFormats.compactDateTime} /> to recognize
          the remaining revenue on project completion.
        </Provenance>
      </Header>

      <Section>
        <Label>Calculation</Label>
        <Rows>
          <Row>
            <span>Billing schedule</span>
            <Amount $strong>
              <Currency value={revenue.billingScheduleRevenue} currency={revenue.currency} />
            </Amount>
          </Row>

          <Row>
            <span>Previously recognized</span>
            <Amount $strong>
              <Operator icon="minus" />
              <Currency value={revenue.previouslyRecognized} currency={revenue.currency} />
            </Amount>
          </Row>
        </Rows>

        <Result>
          <span>Recognized this entry</span>
          <ResultAmount>
            <Currency value={revenue.recognized} currency={revenue.currency} />
          </ResultAmount>
        </Result>
      </Section>
    </Card>
  );
}

function PercentComplete({ date, calculation }) {
  const { method, actualsThrough, progress, revenue } = calculation;

  const measure = (value) =>
    method === 'automated_hours' ? <Hours value={value} /> : <Currency value={value} currency={revenue.currency} />;

  const capped = revenue.nonBillableExpensesToDate > revenue.nonBillableExpensesBudget;
  const forecast = progress.denominatorBasis === 'forecast';
  const beforeActualsThrough = date < actualsThrough;
  const forecastPlanned = progress.forecastPlanned > 0;

  return (
    <Card>
      <Header>
        <Label>How this amount was calculated</Label>
        <Provenance>
          Generated using the &ldquo;{revenueRecognitionMethods[method]?.name}&rdquo; revenue recognition method on{' '}
          <DateTime value={calculation.generatedAt} format={dateFormats.compactDateTime} />.
        </Provenance>
      </Header>

      <Section $shaded>
        <Label>{metrics[method]} values used</Label>
        <Rows $dense>
          <span>Percent complete</span>
          <Derivation $dense>
            <Row>
              <span>
                Actuals through <DateTime value={beforeActualsThrough ? date : actualsThrough} />
              </span>
              <Amount>{measure(progress.actual)}</Amount>
            </Row>

            {progress.planned > 0 && (
              <>
                <Row>
                  <span>
                    Planned through <DateTime value={date} />
                  </span>
                  <Amount>
                    <Operator icon="plus" />
                    {measure(progress.planned)}
                  </Amount>
                </Row>
                <NestedSubtotal>
                  <Amount>{measure(progress.toDate)}</Amount>
                </NestedSubtotal>
              </>
            )}

            <div>
              <Row>
                <span>{forecast ? 'Forecast' : 'Budget'}</span>
                <Amount>
                  <Operator icon="divide" />
                  {measure(progress.denominator)}
                </Amount>
              </Row>

              {forecast && (beforeActualsThrough || forecastPlanned) && (
                <Breakdown>
                  <NoWrap>
                    ({measure(progress.forecastActual)} actuals
                    {beforeActualsThrough && (
                      <>
                        {' '}
                        through <DateTime value={actualsThrough} />
                      </>
                    )}
                    {!forecastPlanned && ')'}
                  </NoWrap>
                  {forecastPlanned && (
                    <>
                      {' '}
                      <NoWrap>+ {measure(progress.forecastPlanned)} planned)</NoWrap>
                    </>
                  )}
                </Breakdown>
              )}
            </div>
          </Derivation>
          <Subtotal>
            <Amount $strong>
              <Percent value={progress.percentComplete} />
            </Amount>
          </Subtotal>
        </Rows>
      </Section>

      <Section>
        <Label>Calculation</Label>
        <Rows>
          <Rows $dense>
            <span>Revenue earned</span>
            <Derivation $dense>
              <Row>
                <span>Budget services revenue</span>
                <Amount>
                  <Currency value={revenue.base} currency={revenue.currency} />
                </Amount>
              </Row>
              <Row>
                <span>Percent complete</span>
                <Amount>
                  <Operator icon="xmark" />
                  <Percent value={progress.percentComplete} />
                </Amount>
              </Row>
            </Derivation>
            <Subtotal>
              <Amount $strong>
                <Currency value={revenue.earned} currency={revenue.currency} />
              </Amount>
            </Subtotal>
          </Rows>

          {capped ? (
            <Rows $dense>
              <span>Non-billable expenses</span>
              <Derivation $dense>
                <Row>
                  <span>To date</span>
                  <Amount>
                    <Currency value={revenue.nonBillableExpensesToDate} currency={revenue.currency} />
                  </Amount>
                </Row>
                <Row>
                  <span>Budget</span>
                  <Amount>
                    <Currency value={revenue.nonBillableExpensesBudget} currency={revenue.currency} />
                  </Amount>
                </Row>
              </Derivation>
              <Subtotal>
                <Amount $strong>
                  <Operator icon="plus" />
                  <Currency value={revenue.nonBillableExpensesRecognizable} currency={revenue.currency} />
                </Amount>
              </Subtotal>
            </Rows>
          ) : (
            <Row>
              <span>Non-billable expenses</span>
              <Amount $strong>
                <Operator icon="plus" />
                <Currency value={revenue.nonBillableExpensesRecognizable} currency={revenue.currency} />
              </Amount>
            </Row>
          )}

          <Row>
            <span>Previously recognized</span>
            <Amount $strong>
              <Operator icon="minus" />
              <Currency value={revenue.previouslyRecognized} currency={revenue.currency} />
            </Amount>
          </Row>
        </Rows>

        <Result>
          <span>Recognized this entry</span>
          <ResultAmount>
            <Currency value={revenue.recognized} currency={revenue.currency} />
          </ResultAmount>
        </Result>
      </Section>
    </Card>
  );
}

export default function RevenueRecognitionCalculation({ entry }) {
  switch (entry.calculation.formula.id) {
    case 'percent_complete':
      return <PercentComplete date={entry.date} calculation={entry.calculation} />;

    case 'remaining_revenue':
      return <RemainingRevenue calculation={entry.calculation} />;

    default:
      return null;
  }
}
